// src/services/appleRemindersService.js
//
// Copies Worthly reminders into a "Worthly" list in the iOS Reminders app, so
// they survive independently of this app.
//
// Strictly one way. We never read the user's other reminders, and nothing that
// happens in the Reminders app flows back here.

import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';

const LIST_TITLE = 'Worthly';

const isIos = () => Capacitor.getPlatform() === 'ios';

/** 'YYYY-MM-DDTHH:mm' (local) -> epoch ms. Not Date.parse, which treats it as UTC. */
export function dueAtToMs(dueAt) {
  if (!dueAt) return null;
  const [d, t] = String(dueAt).split('T');
  const [y, m, day] = d.split('-').map(Number);
  const [hh, mm] = (t || '12:00').split(':').map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day, hh || 0, mm || 0, 0, 0).getTime();
}

/**
 * Ask for access. Resolves 'granted' or 'denied' - never 'prompt'.
 * Throws if the Info.plist keys are missing, which is why patch070 came first.
 */
export async function requestAccess() {
  if (!isIos()) return 'denied';
  try {
    const res = await CapacitorCalendar.requestFullRemindersAccess();
    return res.result;
  } catch (err) {
    console.warn('[AppleReminders] Access request failed:', err);
    return 'denied';
  }
}

/**
 * Return a usable list id, creating the Worthly list if needed.
 *
 * A stored id is verified against the device first: someone can delete the
 * whole list in the Reminders app, and a stale id would make every write fail
 * quietly afterwards.
 */
export async function ensureList(storedListId) {
  if (!isIos()) return null;

  try {
    const { result: lists } = await CapacitorCalendar.getRemindersLists();

    if (storedListId && (lists || []).some((l) => l.id === storedListId)) {
      return storedListId;
    }

    // Reuse a list we made previously before making a second one.
    const existing = (lists || []).find((l) => l.title === LIST_TITLE);
    if (existing) return existing.id;

    const created = await CapacitorCalendar.createRemindersList({
      title: LIST_TITLE,
      // Neutral across all three app themes - pink only suited one of them.
      color: 'green',
    });
    return created.id || created.result || null;
  } catch (err) {
    console.warn('[AppleReminders] Could not resolve the list:', err);
    return null;
  }
}

function fullUrl(link) {
  const l = String(link || '').trim();
  if (!l) return null;
  return /^https?:\/\//i.test(l) ? l : 'https://' + l;
}

function optionsFor(reminder, listId) {
  const ms = dueAtToMs(reminder.dueAt);
  const url = fullUrl(reminder.link);

  // The url field is set, but Apple's Reminders UI does not surface it
  // anywhere you can see or tap - so the link goes in the notes body too,
  // where it is visible and tappable.
  const noteParts = [];
  if (reminder.note) noteParts.push(reminder.note);
  if (url) noteParts.push(url);
  const notes = noteParts.join('\n\n');

  return {
    title: reminder.title || 'Reminder',
    listId,
    // startDate matters: alerts are relative to the START, so a reminder with
    // only a dueDate would never actually alert.
    startDate: ms,
    dueDate: ms,
    alerts: [0],
    isCompleted: !!reminder.done,
    ...(notes ? { notes } : {}),
    ...(url ? { url } : {}),
  };
}

/** Create the Apple copy. Returns its id, or null. */
export async function createCopy(reminder, listId) {
  if (!isIos() || !listId) return null;
  try {
    const res = await CapacitorCalendar.createReminder(optionsFor(reminder, listId));
    return res.id || null;
  } catch (err) {
    console.warn('[AppleReminders] Create failed:', err);
    return null;
  }
}

/** Update an existing copy. Returns true if it is still there and was updated. */
export async function updateCopy(appleId, reminder, listId) {
  if (!isIos() || !appleId) return false;
  try {
    await CapacitorCalendar.modifyReminder({ id: appleId, ...optionsFor(reminder, listId) });
    return true;
  } catch (err) {
    // Most likely someone deleted it in the Reminders app.
    console.warn('[AppleReminders] Update failed:', err);
    return false;
  }
}

/** Remove a copy - used when the reminder is deleted in Worthly. */
export async function deleteCopy(appleId) {
  if (!isIos() || !appleId) return;
  try {
    await CapacitorCalendar.deleteReminder({ id: appleId });
  } catch (err) {
    console.warn('[AppleReminders] Delete failed:', err);
  }
}

/**
 * Short signature of everything that ends up in the Apple copy.
 *
 * Stored next to the Apple id so a later pass can tell "unchanged" from
 * "edited since export" without writing anything to find out.
 */
export function signatureFor(reminder) {
  const parts = [
    reminder.title || '',
    reminder.dueAt || '',
    reminder.note || '',
    reminder.link || '',
    reminder.done ? '1' : '0',
  ].join('\u0001');

  let h = 5381;
  for (let i = 0; i < parts.length; i += 1) {
    h = ((h * 33) ^ parts.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}

/**
 * Bring every reminder into line with its Apple copy, writing as little as
 * possible.
 *
 * Returns { listId, ids, stats } where ids maps our reminder id to
 * { appleId, sig } for the caller to persist. A reminder whose copy exists and
 * whose signature still matches is left completely alone.
 */
export async function syncAll(reminders, storedListId, opts = {}) {
  if (!isIos()) return { listId: null, ids: {}, stats: { created: 0, updated: 0, skipped: 0 } };

  const listId = await ensureList(storedListId);
  if (!listId) return { listId: null, ids: {}, stats: { created: 0, updated: 0, skipped: 0 } };

  // ensureList only hands back a different id when the stored list is gone
  // from the device - deleting a list deletes every copy in it, so every
  // saved Apple id is stale and everything is exported again.
  const listReplaced = !!storedListId && listId !== storedListId;
  // Set when the user switches the backup on. They are asking for the list
  // to be complete, so a missing copy is recreated rather than left gone.
  const recreateMissing = !!opts.recreateMissing;

  const ids = {};
  const stats = { created: 0, updated: 0, skipped: 0, missing: 0, recreated: 0 };

  for (const r of (reminders || [])) {
    if (!r || !r.dueAt) continue;
    const sig = signatureFor(r);

    if (r.appleId && !listReplaced) {
      if (r.appleSig === sig && !recreateMissing) {
        // Unchanged since it was exported - nothing to do, and no call made.
        ids[r.id] = { appleId: r.appleId, sig };
        stats.skipped += 1;
        continue;
      }
      // Edited here, so push the change across.
      const ok = await updateCopy(r.appleId, r, listId);
      if (ok) {
        ids[r.id] = { appleId: r.appleId, sig };
        stats.updated += 1;
      } else if (recreateMissing) {
        // Switching the backup on is a request for a complete list.
        const again = await createCopy(r, listId);
        if (again) {
          ids[r.id] = { appleId: again, sig };
          stats.recreated += 1;
        }
      } else {
        // The copy was deleted in the Reminders app. Deleted there means gone;
        // we do not recreate it. Keep the record as it was so we are not
        // repeatedly writing over a copy that no longer exists.
        stats.missing += 1;
      }
      continue;
    }

    // Never exported before.
    const created = await createCopy(r, listId);
    if (created) {
      ids[r.id] = { appleId: created, sig };
      stats.created += 1;
    }
  }

  return { listId, ids, stats };
}

/** Open the Reminders app - handy straight after a first sync. */
export async function openRemindersApp() {
  if (!isIos()) return;
  try {
    await CapacitorCalendar.openReminders();
  } catch (err) {
    console.warn('[AppleReminders] Could not open Reminders:', err);
  }
}
